/**!
 * Created by Leonardo Cheslacki on 11/11/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersIcon, HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {_360: {speed, slide}}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class Stage360 extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        const {current_name} = this.props;

        this.state = {};

        this.$big_frame = this.$controller.big_1;

        this.$last_coordinate_slide = null;

        this.$reference360 = null;

        this.$thisFrame = null;

        this.$helpersIcon = HelpersIcon(this)({current_name: current_name});
        this.$helpersPin = HelpersPin(this)({current_name: current_name});

        this.$supportTouchCanvas = null;

        _this = this;
    };

    $functions = {

        /**
         * Stop Model
         */
        handleSlideMouseDown: function () {
            _this.destroyTicker('_handleSlideSync360');
        },

        /**
         * Move slide and model.
         * @param big_positionX
         */
        handleSlidePressMove: function ({big_positionX}) {
            _this.setPosition(big_positionX);
        },

        /**
         * Stop Sun and position slide.
         * @param big_clientX
         * @param big_clientY
         */
        handleSlideTouchStart: function ({big_clientX, big_clientY}) {
            const {occ_slider} = _this.$thisFrame;

            _this.setPosition(big_clientX.minus(new Big(occ_slider.x)), _this.$supportTouchCanvas.checkLimitation(occ_slider, big_clientX, big_clientY, occ_slider.x, occ_slider.y));

            _this.destroyTicker('_handleSlideSync360');
        },

        /**
         * Move slide and sun.
         * @param event
         * @param big_clientX
         * @param big_clientY
         */
        handleSlideTouchMove: function (event, {big_clientX, big_clientY}) {
            _this.stopPropagation(event);

            const {occ_slider} = _this.$thisFrame;

            //_this.testCoordinate(stage, big_clientX, big_clientY);

            _this.setPosition(big_clientX.minus(new Big(occ_slider.x)), _this.$supportTouchCanvas.checkLimitation(occ_slider, big_clientX, big_clientY, occ_slider.x, occ_slider.y));
        },

        /**
         *
         * @param event
         * @param data
         */
        handleSlideDegrees: function (event, {data}) {
            const
                {occ_plant: {occ_effect, occ_effect: {labels, totalFrames}}} = _this.$thisFrame,
                {big_slider_width, big_slide_width, big_limit, big_max, big_position, big_speed, label} = data;

            let stop = false;

            if (_this.$big_frame.gte(big_position)) {
                _this.$big_frame = _this.$big_frame.minus(big_speed);
                _this.$big_frame.lte(big_position) && (stop = true);
            } else if (_this.$big_frame.lte(big_position)) {
                _this.$big_frame = _this.$big_frame.plus(big_speed);
                _this.$big_frame.gte(big_position) && (stop = true);
            }

            let
                original = big_slider_width.minus(big_slide_width),
                target = _this.$big_frame.minus(big_limit),
                coordinateSlide = _this.$big_frame;

            if (stop) {
                occ_effect.gotoAndStop(label);

                coordinateSlide = original.div(new Big(labels.length - 1)).times(new Big(_this.toNumber(label) - 1)).plus(big_limit);

                _this.$reference360.setCoordinateSlide(_this.bigToNumber(coordinateSlide, false, 'int'));

                _this.destroyTicker('_handleSlideSync360');
            } else {
                if (coordinateSlide.lt(big_limit)) {
                    coordinateSlide = big_limit;
                } else if (coordinateSlide.gt(big_max)) {
                    coordinateSlide = big_max;
                }

                _this.$reference360.setCoordinate360(_this.calcSize(original, target, totalFrames - 1, false, 'int'));
                _this.$reference360.setCoordinateSlide(_this.bigToNumber(coordinateSlide, false, 'int'));
            }
        },

        /**
         *
         * @param event
         * @param label
         */
        handleDegrees: function (event, {label}) {
            _this.syncDegrees(label);
        }
    };

    /**
     *
     * @param label
     * @returns {*}
     */
    findLabel(label = null) {
        const {occ_plant: {occ_effect: {labels}}} = _this.$thisFrame;

        if (this.isEmpty(label)) {
            return this.lastArray(labels);
        } else {
            return labels.find((value) => (value.label === label));
        }
    };

    /**
     *
     * @param big_positionX
     * @param enabled
     */
    setPosition = (big_positionX, enabled = true) => {
        const {occ_slider, occ_plant: {occ_effect: {totalFrames}}, occ_slider: {occ_slide}} = this.$thisFrame;

        let
            big_slider_width = (new Big(occ_slider.nominalBounds.width)),
            big_slide_width = (new Big(occ_slide.nominalBounds.width)),
            big_limit = big_slide_width.div(this.$controller.big_2);

        /**
         * Limitation slide.
         */
        if (big_positionX.gte(big_limit) && big_positionX.lte(big_slider_width.minus(big_limit)) && enabled) {
            this.$reference360.setCoordinate360(this.calcSize(big_slider_width.minus(big_slide_width), big_positionX.minus(big_limit), totalFrames - 1, false, 'int'));
            this.$reference360.setCoordinateSlide(this.bigToNumber(big_positionX, false, 'int'));
        }
    };

    setColors = () => {
        const
            {
                occ_slider_title,
                occ_slider,
            } = this.$thisFrame;

        occ_slider_title.occ_left.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_center.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_right.color = this.tintToHex(slide.color_title);

        for (let property of occ_slider.occ_rule.occ_line.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        for (let property of occ_slider.occ_rule.occ_ruler.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        this.$helpersPin.setColor(occ_slider.occ_slide.occ_round, slide.color_round);
        this.$helpersPin.setColor(occ_slider.occ_slide.occ_color, slide.color_circle);
    };

    /**
     *
     * @param _this
     * @returns {{reference(): void, setCoordinateSlide(*): void, setCoordinate360(*): void, _360, object: null}|null}
     */
    reference360 = (_this) => {
        return {
            object: null,

            /**
             *
             * @returns {null}
             */
            get _360() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set _360(object) {
                this.object = object;
            },

            reference() {
                const {occ_plant: {occ_effect, occ_effect: {occ_guide}}, occ_slider} = _this.$thisFrame;
                this._360 = {content: occ_effect, guide: occ_guide, input: occ_slider};
            },

            /**
             *
             * @param x
             */
            setCoordinate360(x) {
                const {content} = this.object;

                x = _this.toIntegerOrFloat(x);

                if (_this.isIntegerOrFloat(x)) {
                    content.gotoAndStop(_this.toInteger(x));
                }
            },

            /**
             *
             * @param x
             */
            setCoordinateSlide(x) {
                const {input: {occ_slide}} = this.object;

                x = _this.toIntegerOrFloat(x);

                if (_this.isIntegerOrFloat(x)) {
                    _this.$last_coordinate_slide = x;
                    occ_slide.setTransform(x, occ_slide.y);
                }
            }
        }
    };

    /**
     *
     * @param label
     */
    syncDegrees = (label = null) => {
        const
            {position} = this.findLabel(label),
            {
                occ_slider,
                occ_slider: {occ_slide},
                occ_plant: {occ_effect: {totalFrames}}
            } = this.$thisFrame;

        let
            big_index = (new Big(position)),
            big_length = (new Big(totalFrames - 1)),
            big_slider_width = (new Big(occ_slider.nominalBounds.width)),
            big_slide_width = (new Big(occ_slide.nominalBounds.width)),
            big_limit = big_slide_width.div(this.$controller.big_2),
            big_max = big_slider_width.minus(big_limit),
            big_speed = (new Big(speed)),
            big_position = big_slider_width.minus(big_limit).div(big_length).times(big_index);

        this.$big_frame = !this.isEmpty(this.$last_coordinate_slide) ? (new Big(this.$last_coordinate_slide)) : this.$controller.big_0;

        createjs.Ticker.timingMode = createjs.Ticker.RAF;

        let ticker = createjs.Ticker.on('tick', this.$functions.handleSlideDegrees, false, false, {
            data: {
                big_slider_width: big_slider_width,
                big_slide_width: big_slide_width,
                big_limit: big_limit,
                big_max: big_max,
                big_position: big_position,
                big_speed: big_speed,
                label: label
            }
        });

        this.setReferenceTicker('_handleSlideSync360', ticker);
    };

    start360 = () => {
        let composition = this.getComposition(this.props.current_name);
        this.isEmpty(composition) && (composition = this.getComposition(this.props.current_page));

        const
            {properties} = composition.getLibrary(),
            {
                btn_position01,
                btn_position02,
                btn_position03,
                btn_position04,
                btn_position05,
                occ_slider: {occ_slide}
            } = this.$thisFrame;

        const handle = (event, {type, state}) => {
            switch (type) {
                case '_MOUSE_DOWN_':
                    this.$functions.handleSlideMouseDown();
                    break;
                case '_MOUSE_MOVE_':
                    this.$functions.handleSlidePressMove(state);
                    break;
                case '_TOUCH_START_':
                    this.$functions.handleSlideTouchStart(state);
                    break;
                case '_TOUCH_MOVE_':
                    this.$functions.handleSlideTouchMove(event, state);
                    break;
                default:
            }
        };

        this.$supportTouchCanvas = this.main.$supportTouchCanvas({
            occ: occ_slide,
            properties: properties,
            callback: handle
        });

        this.$supportTouchCanvas.initTouch();

        this.setHandleMouseDown(btn_position01, this.$functions.handleDegrees, {label: 'position01'});

        this.setHandleMouseDown(btn_position02, this.$functions.handleDegrees, {label: 'position02'});

        this.setHandleMouseDown(btn_position03, this.$functions.handleDegrees, {label: 'position03'});

        this.setHandleMouseDown(btn_position04, this.$functions.handleDegrees, {label: 'position04'});

        this.setHandleMouseDown(btn_position05, this.$functions.handleDegrees, {label: 'position05'});

        this.syncDegrees('position04');
    };

    init360 = () => {
        this.$thisFrame = this.getFrame();
        this.$reference360 = this.reference360(this);
        this.$reference360.reference();
        this.$helpersIcon.setIcon(this.$thisFrame);
        this.setColors();
        this.start360();
    };
}

/**
 *
 * @type {{}}
 */
Stage360.propTypes = {
    current_name: PropTypes.string,
    current_page: PropTypes.string
};

/**
 *
 * @type {{}}
 */
Stage360.defaultProps = {
    current_name: null,
    current_page: null

};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(Stage360, {className: 'Stage360'});
